'use client';
import React from 'react';
import { TypePlayKitPageTranslations } from '@/utils/translations/types/TypePlayKitPageTranslations';
import { HeaderSize, HeaderText } from '@/components/ui/HeaderText';
import { PlayKitCard } from '@/components/account';
import { interpolate } from '@/utils/translations';
import useLocale from '@/hooks/useLocale';
import classNames from 'classnames';
import {
  generateBookAddonLink,
  getBookBundleUpsellProductDetails,
  trackClickEvent,
  TypeScheduledItemGroup,
} from '@/components/account/PlayKitSchedule/utils';
import {
  BookBundleUpsell,
  OrderInsights,
} from '@/components/account/PlayKitSchedule/components';
import { formatMoney } from '@/utils/helpers';
import { TypeBannerFields } from '@/core/generated-types';
import CmsBanner from '@/components/generics/GenBanner/CmsBanner';
import { Entry } from 'contentful';
import { constructLegacyCustomerPortalLink } from '@/utils/shopSubdomain';
import { CTA_NAMES } from '@/utils/rudderstack';
import { BILLING_STATUS } from '@/lib/api/subscriptionManager/types';

export interface CurrentPlayKitProps {
  t: TypePlayKitPageTranslations;
  group: TypeScheduledItemGroup;
  noticeBanner?: Entry<TypeBannerFields>;
}

function CurrentPlayKit({ t, group, noticeBanner }: CurrentPlayKitProps) {
  const locale = useLocale();
  const { shouldShowBookBundleUpsell, bookBundleUpsellProducts } =
    getBookBundleUpsellProductDetails(group);

  const isPrepaid =
    group.items.length > 1 ||
    group.items[0].billing_status === BILLING_STATUS.PREPAY;

  function buildBannerProps() {
    const subscriptionExtensionLink = constructLegacyCustomerPortalLink(
      locale,
      [
        group.userUuid,
        'subscriptions',
        group.subscriptionUuid,
        'gift-extension',
      ],
    );
    const extendedCtaFields = {
      ...noticeBanner?.fields.cta?.fields,
      linkUrl: subscriptionExtensionLink,
    };

    return {
      ...noticeBanner?.fields,
      cta: {
        ...noticeBanner?.fields.cta,
        fields: extendedCtaFields,
      },
    } as unknown as TypeBannerFields;
  }

  const bannerProps = buildBannerProps();

  return (
    <div data-testid="currentPlayKit">
      <HeaderText
        classes="text-3xl md:text-4xl pb-1 md:pb-2"
        size={HeaderSize.H2}
        text={isPrepaid ? t.currentOrder : t.currentPlayKit}
      />
      {group.savingsText && (
        <p className="mb-4 text-base md:text-lg">{group.savingsText}</p>
      )}
      {group.order && (
        <OrderInsights
          t={t}
          group={group}
          locale={locale}
          showOrderDate={!group.isGiftOrder}
          showGiftActivationDate={group.isGiftOrder}
          showOrderNumber
        />
      )}
      {group.isPartialSub && bannerProps && (
        <div className="pb-4 md:pb-6">
          <CmsBanner
            {...bannerProps}
            testId="partial-subscription-extension-banner"
            onClickCta={() => {
              trackClickEvent(CTA_NAMES.GIFT_EXTENSION_FLOW_STARTED);
            }}
          />
        </div>
      )}
      <div
        className={classNames('rounded-md p-6 mb-10', {
          'bg-indigo-tint ': isPrepaid,
          'shadow-lg': !isPrepaid,
        })}
      >
        {group.items.map((item, index) => {
          const isLastKitInGroup = index === group.items.length - 1;
          return (
            <div key={item.uuid}>
              {t.shippingNext && index === 1 && (
                <HeaderText
                  classes="pb-4"
                  size={HeaderSize.H3}
                  text={t.shippingNext}
                />
              )}
              <PlayKitCard
                t={t}
                key={item.uuid}
                scheduledItem={item}
                isLastKitInGroup={isLastKitInGroup}
              >
                {isLastKitInGroup && shouldShowBookBundleUpsell && (
                  <BookBundleUpsell
                    title={t.bookBundleAddOn}
                    linkHref={generateBookAddonLink(group, locale)}
                    linkCopy={interpolate(t.addBookBundles, {
                      bundleAmount: formatMoney(
                        bookBundleUpsellProducts[0].metadata.price_cents / 100,
                        locale,
                        true,
                      ),
                    })}
                    bookBundleUpsellProducts={bookBundleUpsellProducts}
                  />
                )}
              </PlayKitCard>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CurrentPlayKit;
